/**
 * OfflineBanner — thin status strip that appears whenever the browser is
 * offline OR the write queue has pending items. Auto-hides once we're back
 * online and the queue has drained.
 *
 * Kept intentionally small so it can sit above the topbar or above the POS
 * shell without stealing real estate.
 */
import { CloudOff, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { count as queueCount } from './offlineQueue';
import { onQueueChange, syncNow } from './syncWorker';

export function OfflineBanner() {
  const [isOffline, setIsOffline] = useState<boolean>(
    () => typeof navigator !== 'undefined' && navigator.onLine === false,
  );
  const [pending, setPending] = useState<number>(0);
  const [syncing, setSyncing] = useState<boolean>(false);

  useEffect(() => {
    const onOnline = () => setIsOffline(false);
    const onOffline = () => setIsOffline(true);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const refresh = () => {
      void queueCount().then((n) => {
        if (!cancelled) setPending(n);
      });
    };
    refresh();
    const unsub = onQueueChange(refresh);
    // Also poll every few seconds — the sync worker's own notifications cover
    // most changes, but this catches the "queue drained by another tab" case.
    const iv = setInterval(refresh, 5000);
    return () => {
      cancelled = true;
      unsub();
      clearInterval(iv);
    };
  }, []);

  const visible = isOffline || pending > 0;
  if (!visible) return null;

  const handleSync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      await syncNow();
    } finally {
      setSyncing(false);
    }
  };

  const message = isOffline
    ? pending > 0
      ? `Working offline — ${pending} change${pending === 1 ? '' : 's'} queued`
      : 'Working offline'
    : `${pending} change${pending === 1 ? '' : 's'} pending sync`;

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center justify-between gap-3 border-b border-amber-300/70 bg-amber-100 px-4 py-1.5 text-xs text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/60 dark:text-amber-100"
    >
      <div className="flex min-w-0 items-center gap-2">
        <CloudOff className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="truncate font-medium">{message}</span>
        {pending > 0 && (
          <Badge variant="warning" size="sm" className="ml-1">
            {pending}
          </Badge>
        )}
      </div>
      {!isOffline && pending > 0 && (
        <Button
          size="sm"
          variant="outline"
          leftIcon={<RefreshCw className={syncing ? 'animate-spin' : undefined} />}
          onClick={handleSync}
          disabled={syncing}
          className="h-7 px-2 text-[11px]"
        >
          {syncing ? 'Syncing…' : 'Sync now'}
        </Button>
      )}
    </div>
  );
}
